"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SETUP_PROMPT } from "@/lib/marketing/setup-prompt";
import { captureProductEvent } from "@/lib/posthog/product-events";

export function useSetupPromptCopy(prompt = SETUP_PROMPT, surface: "website" | "account" = "website") {
  const [state, setState] = useState<"idle" | "copying" | "copied" | "manual">("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fallback = useRef<HTMLTextAreaElement>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  useEffect(() => { if (state === "manual") { fallback.current?.focus(); fallback.current?.select(); } }, [state]);

  async function copy(location: string, provider?: { name: string; icon: string }) {
    if (timer.current) clearTimeout(timer.current);
    setState("copying");
    captureProductEvent("cta_clicked", { surface, location, target: provider ? `setup_${location}` : "copy_setup_prompt" });
    try {
      await navigator.clipboard.writeText(prompt);
      setState("copied");
      timer.current = setTimeout(() => setState("idle"), 2500);
    } catch {
      setState("manual");
    }
  }

  return {state, copy, fallback};
}

export function SetupPromptFallback({ inputRef, prompt = SETUP_PROMPT }: { inputRef: React.RefObject<HTMLTextAreaElement | null>; prompt?: string }) {
  return <div className="conversion-prompt-fallback"><p role="alert">Copy this prompt and paste it into your coding assistant.</p><textarea ref={inputRef} aria-label="Setup prompt" readOnly value={prompt} onFocus={event => event.currentTarget.select()} /></div>;
}

export default function CopySetupPrompt({ location, outline = false, provider, prompt = SETUP_PROMPT, label = "Copy setup prompt", ariaLabel, surface = "website" }: { location: string; outline?: boolean; provider?: { name: string; icon: string }; prompt?: string; label?: string; ariaLabel?: string; surface?: "website" | "account" }) {
  const {state, copy, fallback} = useSetupPromptCopy(prompt, surface);
  return <div className={`conversion-prompt-action conversion-prompt-action-${location}${provider ? " conversion-provider-copy" : ""}`}>
    <Button type="button" size={location === "header" ? "sm" : surface === "account" ? "default" : "lg"} variant={provider ? "ghost" : outline ? "outline" : "default"} aria-label={provider ? `Copy setup prompt for ${provider.name}` : state === "copied" ? "Copied" : ariaLabel} title={provider ? `Copy setup prompt for ${provider.name}` : undefined} disabled={state === "copying"} onClick={() => copy(location, provider)}>
      {provider && state === "copied" ? <Check className="size-5" aria-hidden="true" /> : provider ? <Image className={`conversion-provider-logo${provider.icon.endsWith("-color") ? " is-color" : ""}`} src={`/icons/providers/${provider.icon}.svg`} alt="" width={20} height={20} /> : state === "copied" ? <Check data-icon="inline-start" aria-hidden="true" /> : <Copy data-icon="inline-start" aria-hidden="true" />}
      <span>{provider?.name ?? (state === "copied" ? "Copied" : label)}</span>
    </Button>
    <span role="status" className="sr-only">{state === "copied" ? provider ? `Prompt copied for ${provider.name}.` : "Setup prompt copied." : ""}</span>
    {state === "manual" ? <div className="conversion-prompt-fallback">
      <p role="alert">Copy this prompt and paste it into your coding assistant.</p>
      <textarea ref={fallback} aria-label="Setup prompt" readOnly value={prompt} onFocus={event => event.currentTarget.select()} />
    </div> : null}
  </div>;
}
